import { QuestionStatus } from '@report/graphql';
import { Label, LabelColor, styled, TableCell, TableRow } from '@shared/components';
import { formatDateTh } from '@shared/utils';

import { useItemAnalysisYearlySearchParams } from '../../hooks';

const QUESTION_STATUS_COLOR: Record<QuestionStatus, LabelColor> = {
  [QuestionStatus.Published]: LabelColor.SUCCESS,
  [QuestionStatus.Disabled]: LabelColor.GREY,
};

const QUESTION_STATUS_TEXT: Record<QuestionStatus, string> = {
  [QuestionStatus.Published]: 'Published',
  [QuestionStatus.Disabled]: 'Disabled',
};

export interface ItemAnalysisResponseStatisticsTableBodyProps {
  rows: {
    questionRefCode: string;
    firstPublish: Date;
    lastPublish: Date;
    type: string;
    status: QuestionStatus;
    pInitial: number | null;
    pUsed: number | null;
    bIndex: number | null;
    yearlyData: {
      year: number;
      examinees: number | null;
      pCalculated: number | null;
      bIndex: number | null;
    }[];
    summary: {
      examinees: number | null;
      pCalculated: number | null;
      bIndex: number | null;
    };
  }[];
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderColor: theme.palette.grey[300],
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  ...theme.typography.body2Semibold,
}));

export default function ItemAnalysisYearlyTableBody(
  props: Readonly<ItemAnalysisResponseStatisticsTableBodyProps>,
) {
  const [{ pageSize, page }] = useItemAnalysisYearlySearchParams();
  const { rows } = props;

  return (
    <>
      {rows.map((row, rowIndex) => {
        return (
          <TableRow key={row.questionRefCode} sx={{ border: 'none', maxHeight: '40px' }}>
            <StyledTableCell align='center' sx={{ minWidth: '64px' }}>
              {rowIndex + 1 + page * pageSize}
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: '200px', p: (theme) => theme.spacing(1.5, 2) }}>
              {row.questionRefCode}
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: '110px', p: (theme) => theme.spacing(1.5, 2) }}>
              {formatDateTh(row.firstPublish)}
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: '110px', p: (theme) => theme.spacing(1.5, 2) }}>
              {formatDateTh(row.lastPublish)}
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: '100px', p: (theme) => theme.spacing(1.5, 2) }}>
              {row.type}
            </StyledTableCell>
            <StyledTableCell sx={{ minWidth: '100px', p: (theme) => theme.spacing(1.5, 2) }}>
              <Label
                color={QUESTION_STATUS_COLOR[row.status]}
                sx={{ borderRadius: (theme) => theme.shape.rounded(7) }}
                size='small'
              >
                {QUESTION_STATUS_TEXT[row.status]}
              </Label>
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ minWidth: '100px' }}>
              {row.pInitial === null ? '-' : row.pInitial.toFixed(2)}
            </StyledTableCell>
            <StyledTableCell align='center' sx={{ minWidth: '100px' }}>
              {row.pUsed === null ? '-' : row.pUsed.toFixed(2)}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '100px',
                borderRight: (theme) => `1px solid ${theme.palette.grey[900]}`,
              }}
            >
              {row.bIndex ? row.bIndex.toFixed(2) : '-'}
            </StyledTableCell>
            {row.yearlyData.map((yearData, yearIndex) => {
              return (
                <>
                  <StyledTableCell
                    align='center'
                    sx={{
                      minWidth: '124px',
                    }}
                  >
                    {yearData.examinees}
                  </StyledTableCell>
                  <StyledTableCell align='center' sx={{ minWidth: '100px' }}>
                    {yearData.pCalculated === null ? '-' : yearData.pCalculated.toFixed(2)}
                  </StyledTableCell>
                  <StyledTableCell
                    align='center'
                    sx={{
                      minWidth: '100px',
                      borderRight:
                        yearIndex === row.yearlyData.length - 1
                          ? 'none'
                          : (theme) => `1px solid ${theme.palette.grey[900]}`,
                    }}
                  >
                    {yearData.bIndex === null ? '-' : yearData.bIndex.toFixed(2)}
                  </StyledTableCell>
                </>
              );
            })}
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '124px',
                borderLeft: (theme) => `1px solid ${theme.palette.grey[900]}`,
                backgroundColor: (theme) => theme.palette.grey[100],
              }}
            >
              {row.summary.examinees}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{ minWidth: '100px', backgroundColor: (theme) => theme.palette.grey[100] }}
            >
              {row.summary.pCalculated === null ? '-' : row.summary.pCalculated.toFixed(2)}
            </StyledTableCell>
            <StyledTableCell
              align='center'
              sx={{
                minWidth: '100px',
                borderRight: (theme) => `1px solid ${theme.palette.grey[900]}`,
                backgroundColor: (theme) => theme.palette.grey[100],
              }}
            >
              {row.summary.bIndex === null ? '-' : row.summary.bIndex.toFixed(2)}
            </StyledTableCell>
          </TableRow>
        );
      })}
    </>
  );
}
